import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  consolidationApi,
  type ConsolidationDashboard,
  type ConsolidationReport,
  type DomainOwnership,
} from '../../api/consolidation';

const COMMERCE_MODULES = [
  'catalog', 'productstructure', 'productattributes', 'pricing', 'inventory',
  'listings', 'marketplacesync', 'marketplaceaccount', 'orders', 'warehouseexec',
  'logistics', 'returns', 'financecommerce', 'procurement', 'supplier', 'crm',
];

export function ArchitectureConsolidationPage() {
  const [dashboard, setDashboard] = useState<ConsolidationDashboard | null>(null);
  const [domains, setDomains] = useState<DomainOwnership[]>([]);
  const [selected, setSelected] = useState('catalog');
  const [report, setReport] = useState<ConsolidationReport | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void consolidationApi.dashboard().then(setDashboard).catch((e) => setError(String(e)));
    void consolidationApi.registry().then((r) => setDomains(r.domains)).catch(() => undefined);
  }, []);

  useEffect(() => {
    void consolidationApi.moduleReport(selected).then(setReport).catch(() => setReport(null));
  }, [selected]);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <Link to="/modules" className="text-sm text-blue-600 hover:underline dark:text-blue-400">← Module Dashboard</Link>
      <h1 className="mt-2 text-2xl font-semibold">Architecture Consolidation</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        DEV-000057 — canonical domain ownership, duplicate elimination and dependency simplification.
      </p>
      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Domains</p>
          <p className="text-2xl font-semibold">{dashboard?.total_domains ?? domains.length}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Commerce modules</p>
          <p className="text-2xl font-semibold">{dashboard?.total_modules ?? 16}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Duplicates audited</p>
          <p className="text-2xl font-semibold">{dashboard?.duplicates_audited ?? 0}</p>
        </div>
        <div className="rounded-xl border p-4 dark:border-slate-700">
          <p className="text-sm text-slate-500">Circular deps</p>
          <p className="text-2xl font-semibold">{dashboard?.circular_deps ?? 0}</p>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {COMMERCE_MODULES.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setSelected(m)}
            className={`rounded-lg px-3 py-1 text-sm ${selected === m ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'border dark:border-slate-700'}`}
          >
            {m}
          </button>
        ))}
      </div>

      {report && (
        <section className="mt-8 rounded-xl border p-4 dark:border-slate-700">
          <h2 className="text-lg font-medium">Module report — {selected}</h2>
          <p className="text-sm text-slate-500">Canonical owner: {report.canonical_owner}</p>
          <p className="text-sm">Namespace valid: {report.namespace_valid ? 'yes' : 'no'} · Dependencies valid: {report.dependencies_valid ? 'yes' : 'no'}</p>
          <p className="mt-2 text-sm font-medium">Owned aggregates</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {report.owned_aggregates.map((a) => (
              <span key={a} className="rounded bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{a}</span>
            ))}
          </div>
          <p className="mt-3 text-sm font-medium">Shared services</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {report.shared_services.map((s) => (
              <span key={s} className="rounded bg-blue-50 px-2 py-0.5 text-xs dark:bg-blue-950">{s}</span>
            ))}
          </div>
        </section>
      )}

      <section className="mt-8">
        <h2 className="text-lg font-medium">Duplicate elimination audit</h2>
        <div className="mt-3 space-y-2">
          {(dashboard?.audit.entries ?? []).slice(0, 12).map((e) => (
            <div key={e.service_id} className="rounded-lg border p-3 text-sm dark:border-slate-700">
              <p className="font-medium">{e.service_id} → {e.canonical_id}</p>
              <p className="text-slate-500">{e.status}: {e.reason}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-medium">Canonical domain registry</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {domains.map((d) => (
            <div key={d.domain} className="rounded-lg border p-3 dark:border-slate-700">
              <p className="font-medium">{d.domain}</p>
              <p className="text-sm text-slate-500">Owner: {d.owner}</p>
              <p className="text-xs text-slate-400">{d.aggregates.length} aggregates</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
